import { FC, useState } from 'react'
import AddButton from '@organisms/AddButton'
import Modal from '@organisms/Modal'
import ButtonStyle from '@enums/ButtonStyle'
import ScormFinalTestModal from '@modals/ScormFinalTestModal'
import Onboarding from '@templates/Onboarding'
import OnboardingCodes from '@enums/OnboardingCodes'
import useMe from '@hooks/useMe'
import type { FinalTestMethodOption } from './styled'
import {
  finalTestMethodModalStyle,
  FinalTestMethodList,
  FinalTestMethodRadioMark,
  FinalTestMethodRadioLabel,
  FinalTestModalContent,
  FinalTestModalActions,
  FinalTestModalTitle,
  FinalTestSubmitButton,
} from './styled'
import { ONBOARDING_STEPS, ONBOARDING_TITLE } from './onboarding'

type FinalTestCreationMethod = 'manual' | 'xls' | 'scorm'

type Props = {
  courseId: number
  courseIri: string
  onManualCreate?: () => void
  onXlsCreate?: () => void
  onScormCreate?: () => void
}

const METHOD_OPTIONS: FinalTestMethodOption[] = [
  {
    value: 'manual',
    label: 'Заполнить вручную',
  },
  {
    value: 'xls',
    label: 'Загрузить из .xls',
  },
  {
    value: 'scorm',
    label: 'Загрузить SCORM',
  },
]

const AddFinalTestButton: FC<Props> = ({
  courseId,
  courseIri,
  onManualCreate,
  onXlsCreate,
  onScormCreate,
}) => {
  const { isCompanyGroupCurator, isCompanyCurator, isFilialCurator } = useMe()
  const canSeeOnboarding = isCompanyGroupCurator || isCompanyCurator || isFilialCurator

  const [isMethodsModalVisible, setIsMethodsModalVisible] = useState(false)
  const [isScormModalVisible, setIsScormModalVisible] = useState(false)
  const [selectedMethod, setSelectedMethod] =
    useState<FinalTestCreationMethod>('manual')

  const handleOpenMethodsModal = () => {
    setSelectedMethod('manual')
    setIsMethodsModalVisible(true)
  }

  const handleCloseMethodsModal = () => {
    setIsMethodsModalVisible(false)
  }

  const handleSubmitMethod = () => {
    handleCloseMethodsModal()

    if (selectedMethod === 'manual') {
      onManualCreate?.()
      return
    }

    if (selectedMethod === 'xls') {
      onXlsCreate?.()
      return
    }

    if (onScormCreate) {
      onScormCreate()
      return
    }

    setIsScormModalVisible(true)
  }

  const handleCloseScormModal = () => {
    setIsScormModalVisible(false)
    setSelectedMethod('manual')
  }

  return (
    <>
      <AddButton id={`my-course-add-final-test-button-${courseId}`} onClick={handleOpenMethodsModal}>Добавить итоговое тестирование</AddButton>

      <Modal
        visible={isMethodsModalVisible}
        onModalClose={handleCloseMethodsModal}
        width="994px"
        contentStyle={finalTestMethodModalStyle}
      >
        <FinalTestModalContent>
          <FinalTestModalTitle>Выбрать способ создания итогового тестирования</FinalTestModalTitle>
          <FinalTestMethodList>
            {METHOD_OPTIONS.map((option) => {
              const isActive = selectedMethod === option.value

              return (
                <FinalTestMethodRadioLabel
                  id={`final-test-method-${option.value}`}
                  key={option.value}
                  isActive={isActive}
                >
                  <input
                    type="radio"
                    name={`finalTestMethod-${courseId}`}
                    value={option.value}
                    checked={isActive}
                    onChange={() => setSelectedMethod(option.value)}
                  />
                  <FinalTestMethodRadioMark isActive={isActive} />
                  {option.label}
                </FinalTestMethodRadioLabel>
              )
            })}
          </FinalTestMethodList>
          <FinalTestModalActions>
            <FinalTestSubmitButton
              id="final-test-submit-button"
              buttonStyle={ButtonStyle.PRIMARY}
              big
              onClick={handleSubmitMethod}
            >
              Добавить
            </FinalTestSubmitButton>
          </FinalTestModalActions>
          {canSeeOnboarding && (
            <Onboarding
              steps={ONBOARDING_STEPS}
              firstStepTitle={ONBOARDING_TITLE}
              code={OnboardingCodes.my_courses_final_test_create}
            />
          )}
        </FinalTestModalContent>
      </Modal>

      <Modal visible={isScormModalVisible} onModalClose={handleCloseScormModal}>
        <ScormFinalTestModal
          courseId={courseId}
          courseIri={courseIri}
          handleCloseModal={handleCloseScormModal}
        />
      </Modal>
    </>
  )
}

export default AddFinalTestButton
